import React from 'react';
import { Shield, Award, Users, Wrench, Building, Target } from 'lucide-react';

const About: React.FC = () => {
  const features = [
    {
      icon: <Wrench className="h-8 w-8" />,
      title: 'Oficina Mecânica Especializada',
      description: 'Performance e Manutenção com equipamentos de última geração'
    },
    {
      icon: <Building className="h-8 w-8" />,
      title: 'Laboratório de Desenvolvimento',
      description: 'Espaço dedicado para testes e desenvolvimento de soluções'
    },
    {
      icon: <Target className="h-8 w-8" />,
      title: 'Funilaria Artesanal',
      description: 'Projetos personalizados e reparos com acabamento perfeito'
    }
  ];

  const differentials = [
    {
      icon: <Shield className="h-12 w-12" />,
      title: 'Laudo AVCB Aprovado',
      description: 'Um dos diferenciais da Will Performance é a aprovação do Laudo AVCB! Possuímos todas as condições de segurança contra incêndio, garantindo tranquilidade para nossos clientes e funcionários.'
    },
    {
      icon: <Award className="h-12 w-12" />,
      title: 'Mais de 20 Anos de Experiência',
      description: 'Duas décadas de experiência no mercado automotivo, sempre buscando a excelência e inovação em nossos serviços. Nossa trajetória é marcada pela confiança de milhares de clientes satisfeitos.'
    },
    {
      icon: <Users className="h-12 w-12" />,
      title: 'Equipe Especializada',
      description: 'Contamos com profissionais e parceiros especializados em automóveis nacionais e importados. Nossa equipe está sempre atualizada com as mais recentes tecnologias do mercado automotivo.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              OFICINA E FUNILARIA
              <span className="block text-yellow-400">WILL PERFORMANCE</span>
            </h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed">
              Mais de 20 anos de tradição e inovação no mercado automotivo
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Nossa História
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed text-lg">
                <p>
                  A Will Performance é uma Oficina Mecânica localizada na região da Zona Leste de São Paulo, 
                  que atua há mais de 20 anos como oficina mecânica. Hoje, com uma estrutura de mais de 600m², 
                  contamos com uma equipe de profissionais e parceiros especializada em Automóveis Nacionais e Importados.
                </p>
                <p>
                  Em nossa Oficina Mecânica você encontrará toda a estrutura necessária para a realização 
                  dos serviços de manutenção que seu veículo precisa.
                </p>
                <p>
                  Temos muita satisfação em atendê-lo, além de cuidar de seu carro com qualidade e preços justos.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-4 p-6 bg-gray-50 rounded-lg">
                  <div className="text-primary-600 flex-shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Specialization Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              "Um novo conceito em centro automotivo!"
            </h2>
            <div className="max-w-4xl mx-auto space-y-6 text-gray-700 leading-relaxed text-lg">
              <p>
                Contamos também com uma equipe especializada para Carros Turbos, desde manutenção simples, 
                até instalação completa de Turbos e Carros de Arrancadas.
              </p>
              <p>
                Trabalhamos com as melhores marcas do mercado e oferecemos uma mão de obra diferenciada 
                e qualificada para quem quer o MELHOR para seu carro seja nacional ou importado.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Differentials Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nossos Diferenciais
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              O que nos torna únicos no mercado automotivo
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {differentials.map((item, index) => (
              <div 
                key={index} 
                className="text-center p-8 bg-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 group"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="text-primary-600 flex justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-primary-600 transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Commitment */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Compromisso com a Qualidade
            </h2>
            <div className="max-w-4xl mx-auto space-y-6 text-lg leading-relaxed">
              <p>
                Garantimos a entrega de um trabalho de alta qualidade que conquista, dia após dia, 
                a satisfação de nossos clientes no uso dos seus veículos.
              </p>
              <p>
                Temos o compromisso de oferecer o melhor serviço em software de injeção eletrônica 
                para veículos a gasolina e flex, além de customizar o carro do cliente ao seu gosto e necessidade.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Entre em Contato
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Acesse agora para conhecer mais sobre nosso Centro Automotivo ou agende uma visita 
            para a realização de nossos serviços! Caso seja necessário contamos com guinchos 
            para trazer seu veículo até nossa Oficina.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://api.whatsapp.com/send?phone=551123593715"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              WhatsApp
            </a>
            <a
              href="tel:+551123593715"
              className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Ligar Agora
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
